import { Drawer } from '@mui/material';
import { useRef, useState } from 'react';
import styles from './ManageRecipients.module.scss';
import { IconButton } from '../../button/IconButton/IconButton';
import { Icon } from '../../icon/Icon';
import { EntityList } from '../../entity-list/EntityList';
import { RecipientListTable } from './RecipientListTable';
import { Button } from '../../button/Button';
import { RecipientSettingsPane } from './RecipientSettingsPane';
import { Recipient, RecipientActionsType } from './interface';
import { useConfirm } from '../../modal/ConfirmModal/useConfirm';
import { NotificationsAPI } from '../NotificationsAPI';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import {
  createRecipientObjectForUser,
  getFilterKeyMatchValue,
  getRecipientFilters,
  getRecipientsIncludingCurrentUser,
  SEARCH_FILTER_KEY
} from './Utils';
import { matchFilters } from '../../shared/utils/filters';
import { ListConductorRawFilter } from '../../list-conductor/filters';
import { Filters } from '../../filter/Filters';
import { userService } from '../../auth/services/user.service';

export default function ManageRecipientsDrawer({
  activeRecipient,
  closeDrawer,
  open,
  switchRecipient
}: {
  closeDrawer: () => void;
  open: boolean;
  switchRecipient: (value: Recipient) => void;
  activeRecipient: Recipient;
}) {
  const [isRecipientSettingsDrawerOpen, setIsRecipientSettingsDrawerOpen] = useState(false);
  const { confirm } = useConfirm();
  const entityListRef = useRef<{ reset: () => void }>();
  const [addedByList, setAddedByList] = useState<{ label: string; value: string }[]>([]);

  const { fetch, reset } = useInMemoryFilteredList(
    async () => {
      const recipients = await getRecipientsIncludingCurrentUser();

      const uniqueRecipients: string[] = [
        ...new Set(
          recipients
            .filter(recipient => !!recipient.added_by)
            .map((recipient: Recipient) => recipient.added_by)
        )
      ] as string[];
      setAddedByList(
        uniqueRecipients.map((recipient: string) => ({ label: recipient, value: recipient }))
      );
      return recipients;
    },
    (item, params) => {
      return matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item as Recipient)
      );
    }
  );

  const onEditActionClick = () => {};

  const resetRecipientList = () => {
    reset();
    entityListRef.current?.reset();
  };

  const switchRecipientHandler = (recipient: Recipient) => {
    switchRecipient(recipient);
    closeDrawer();
  };

  const deleteRecipient = (data: Recipient) => {
    confirm({
      title: 'Delete Recipient?',
      negativeButtonText: 'Cancel',
      positiveButtonText: 'Delete',
      body: "You won't receive alerts for the recipient",
      onPositiveClickCallback: async () => {
        await NotificationsAPI.deleteRecipient(data.id);
        if (activeRecipient.id === data.id) {
          switchRecipient(createRecipientObjectForUser(userService.getUser()!));
        }
        resetRecipientList();
      }
    });
  };

  return (
    <Drawer open={open} anchor='right' onClose={closeDrawer} keepMounted>
      <div className={styles.drawerContainer} data-id='manage-recipients-drawer'>
        <IconButton
          dataId='manage-recipients-close'
          label=''
          className={styles.closeDrawerBtn}
          onClick={closeDrawer}
        >
          <Icon name='close' />
        </IconButton>

        <div className='px-5 pt-5 pb-0'>
          <div className='text-subheading-2'>Manage Recipient</div>
          <div className='text-secondary text-body-1 mt-1'>
            Switch between recipients, update their Pipeline subscriptions, or add a new recipient
            to your list.
          </div>
        </div>

        <div className='d-flex justify-end px-5 pt-3'>
          <Button
            dataId='add-recipient'
            className='w-100'
            size='lg'
            icon='plus'
            onClick={() => {
              setIsRecipientSettingsDrawerOpen(true);
            }}
          >
            Add New Recipient
          </Button>
        </div>
        <EntityList
          compactControls
          innerRef={entityListRef}
          classes={{ className: styles.listContainer }}
          mode='standalone'
          genericErrorTitle='Error while fetching Recipients'
          genericErrorSubTitle='Retry Loading the Recipients'
          showSearch
          showFilters
          inMemoryList
          searchKey={SEARCH_FILTER_KEY}
          ListComponent={RecipientListTable}
          emptyTitle='No Matching Recipients Found'
          emptySubTitle='Modify search term to find the desired Recipients'
          shimmerColumnsCount={2}
          FiltersComponent={Filters}
          filtersComponentProps={{
            filters: getRecipientFilters(addedByList),
            compactControls: true,
            entity: 'recipient'
          }}
          listComponentProps={{
            activeRecipient,
            actionHandlers: {
              [RecipientActionsType.EDIT]: onEditActionClick,
              [RecipientActionsType.SWITCH]: switchRecipientHandler,
              [RecipientActionsType.DELETE]: deleteRecipient
            },
            tableProps: {
              withoutShadow: true,
              classes: { containerClass: 'border border-radius-md' }
            }
          }}
          onRefresh={() => reset()}
          searchPlaceholder='Search Recipient'
          listIcon='user'
          hidePagination
          dataSource$={fetch}
          entity='recipient'
          newListConnectedTitle=''
        />
      </div>

      <RecipientSettingsPane
        // action={actionTypeRef.current}
        switchRecipient={switchRecipientHandler}
        resetRecipientList={resetRecipientList}
        open={isRecipientSettingsDrawerOpen}
        closeDrawer={() => {
          setIsRecipientSettingsDrawerOpen(false);
        }}
      />
    </Drawer>
  );
}
